/** Player inbox message (`MessageFrontDTO` from UsersAPI). */
export interface PlayerMessage {
  Id: string;
  Title: string;
  Text: string;
  Timestamp: string;
  /** `true` when already read; API names this oddly but it is a boolean. */
  ReadTimeStamp: boolean;
}

/** `CommonRequest` for list (`Value` = machineGuid) or details (`Value` = encrypted recipient id). */
export interface MessagesRequest {
  Value: string;
}

export type MessageBucket = 'all' | 'new' | 'read';

export type MessageStatusLabel = 'New' | 'Read';

/** Display sender — API has no From field; Figma shows Support. */
export const MESSAGE_SENDER = 'Support';

export function isMessageRead(message: Pick<PlayerMessage, 'ReadTimeStamp'>): boolean {
  return Boolean(message.ReadTimeStamp);
}

export function messageStatusLabel(
  message: Pick<PlayerMessage, 'ReadTimeStamp'>,
): MessageStatusLabel {
  return isMessageRead(message) ? 'Read' : 'New';
}

export function filterMessages(
  rows: readonly PlayerMessage[],
  bucket: MessageBucket,
): PlayerMessage[] {
  if (bucket === 'all') return [...rows];
  if (bucket === 'new') return rows.filter((row) => !isMessageRead(row));
  return rows.filter((row) => isMessageRead(row));
}

const MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
] as const;

/** Format like Figma: `18 Oct 2026 09:14`. */
export function formatMessageDateTime(value: string | null | undefined): string {
  if (value == null || value === '') return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  const day = String(date.getDate()).padStart(2, '0');
  const month = MONTHS[date.getMonth()] ?? '';
  const year = date.getFullYear();
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${day} ${month} ${year} ${hours}:${minutes}`;
}

/**
 * Legacy JS `unescape` used by SportsbookApp BindItem for message HTML
 * (`%u0131` → ı, `%20` → space). Prefer this over decodeURIComponent.
 */
export function unescapeLegacy(value: string): string {
  return value
    .replace(/%u([0-9a-fA-F]{4})/g, (_, hex: string) =>
      String.fromCharCode(Number.parseInt(hex, 16)),
    )
    .replace(/%([0-9a-fA-F]{2})/g, (_, hex: string) =>
      String.fromCharCode(Number.parseInt(hex, 16)),
    );
}

const BASIC_HTML_ENTITIES: Record<string, string> = {
  amp: '&',
  lt: '<',
  gt: '>',
  quot: '"',
  apos: "'",
  nbsp: '\u00a0',
};

/** Decode numeric and named HTML entities (`&uuml;` → ü). */
export function decodeHtmlEntities(value: string): string {
  return value.replace(/&(#x?[0-9a-f]+|[a-z][a-z0-9]*);/gi, (match, entity: string) => {
    if (entity.startsWith('#')) {
      const code =
        entity[1] === 'x' || entity[1] === 'X'
          ? Number.parseInt(entity.slice(2), 16)
          : Number.parseInt(entity.slice(1), 10);
      return Number.isFinite(code) ? String.fromCodePoint(code) : match;
    }

    const key = entity.toLowerCase();
    if (BASIC_HTML_ENTITIES[key]) return BASIC_HTML_ENTITIES[key];

    // Named Latin entities (ü, ç, ğ, …) via DOM when available (jsdom in tests).
    if (typeof document !== 'undefined') {
      const el = document.createElement('textarea');
      el.innerHTML = match;
      const decoded = el.value;
      if (decoded && decoded !== match) return decoded;
    }

    return match;
  });
}

/** Decode legacy escapes then HTML entities on message bodies. */
export function decodeMessageText(value: string | null | undefined): string {
  if (value == null || value === '') return '';
  return decodeHtmlEntities(unescapeLegacy(value));
}

/** Strip HTML tags and collapse whitespace for list previews / plain detail body. */
export function stripHtmlToPlain(html: string): string {
  if (!html) return '';
  const withBreaks = html
    .replace(/<\s*br\s*\/?\s*>/gi, '\n')
    .replace(/<\/\s*p\s*>/gi, '\n')
    .replace(/<\/\s*div\s*>/gi, '\n')
    .replace(/<\/\s*li\s*>/gi, '\n');
  const withoutTags = withBreaks.replace(/<[^>]+>/g, '');
  return withoutTags
    .replace(/\u00a0/g, ' ')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

export function messageBodyPlain(message: Pick<PlayerMessage, 'Text'>): string {
  return stripHtmlToPlain(decodeMessageText(message.Text));
}

/** Single-line preview for list rows. */
export function messagePreview(
  message: Pick<PlayerMessage, 'Text'>,
  maxLength = 120,
): string {
  const plain = messageBodyPlain(message).replace(/\s+/g, ' ').trim();
  if (plain.length <= maxLength) return plain;
  return `${plain.slice(0, maxLength - 1).trimEnd()}…`;
}
